/**
 * `<ThreeDModelToolCard />` — WP-6 (3D model / BIM / 3D Tiles ToolCard).
 *
 * Right-rail card for the 3D model layer family. Activates when the
 * `useViewerStore.sceneSelection.toolCardKind` is `'bim'` or `'3dtiles'`
 * (per WP-2 mapping in `app/sceneExplorer.ts`) or when the WP-2
 * `viewer:tool-card-request` `CustomEvent` carries the same
 * `toolCardKind`.
 *
 * Spec references:
 *   - `docs/results/VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md`
 *     §8.6 + §7.6 Card 6.
 *   - §9 P0 — shared `<CoordinateModeBadge />` consumed here and in the
 *     point-cloud card (WP-5).
 *   - `geosoft-platform-design-system/docs/DESIGN_POLICY.md` §5 — tokens
 *     only, single primary CTA per row, 40 px touch min, focus visible,
 *     non-color cue per state.
 *
 * Surface contract: the card is a controlled view that delegates engine
 * mutations to a narrow {@link ThreeDModelCommandAdapter}. Both
 * `Giro3DAdapter` (live) and any future Cesium / Babylon adapter satisfy
 * the surface; the card never imports Three.js or `@giro3d/giro3d`.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
} from 'react';
import type { Layer } from '@geosoft/viewer-sdk';
import {
  Banner,
  Button,
  Chip,
  Icon,
  IconButton,
  tokens,
  type ChipVariant,
  type IconName,
} from '@/design';
import {
  TOOL_CARD_REQUEST_EVENT,
  type ToolCardRequestDetail,
} from '@/app/sceneSelection';
import { useViewerStore } from '@/state/useViewerStore';
import {
  CoordinateModeBadge,
  coordinateModeForLayer,
  type CoordinateMode,
} from './CoordinateModeBadge';
import {
  CAMERA_PRESETS,
  cameraPresetLabel,
  type CameraPreset,
  type ModelBounds,
} from '@/engines/giro3d/cameraPresets';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Render modes the card surfaces. Mirrors the engine's `RenderMode`
 * union (`'default' | 'greyscale' | 'xray' | 'wireframe'`) plus a
 * "shaded" alias for the chip label so the UI strings stay accessible.
 */
export type ThreeDRenderMode = 'default' | 'greyscale' | 'xray' | 'wireframe';

/**
 * Subset of the `'bim' | '3dtiles' | 'glb'` layer-kind union the card
 * recognises. Used by the source-status chip and the title strip.
 */
export type ThreeDModelKind = 'bim' | '3dtiles' | 'glb';

/**
 * BIM / glTF property-set entry. Surfaced in the right-rail property
 * card (IfcGUID + properties). Values are rendered in MONO so the
 * playbook rule for technical identifiers is respected without forcing
 * the entire card to mono.
 */
export interface ThreeDFeatureProperty {
  readonly key: string;
  readonly value: string;
}

/**
 * Selected feature surfaced by the picker. The IfcGUID (when present)
 * is the canonical BIM identifier and is rendered in mono per
 * `ICON_SYSTEM_PLAYBOOK.md` typography rules.
 */
export interface ThreeDFeatureSelection {
  readonly featureId?: string;
  readonly ifcGuid?: string;
  readonly objectName?: string;
  readonly materialName?: string;
  readonly nodeId?: string;
  readonly properties?: readonly ThreeDFeatureProperty[];
}

/** Pure model-tree node consumed by the right-rail card. */
export interface ModelTreeNode {
  readonly id: string;
  readonly name: string;
  readonly visible: boolean;
  readonly children?: readonly ModelTreeNode[];
  readonly featureId?: string;
  readonly count?: number;
}

/**
 * Adapter contract the ToolCard delegates to. Narrow on purpose so both
 * the live `Giro3DAdapter` and stand-in test doubles can satisfy it
 * without dragging in Three.js.
 */
export interface ThreeDModelCommandAdapter {
  setRenderMode(mode: ThreeDRenderMode): void;
  getRenderMode(): ThreeDRenderMode;
  setCameraPreset(preset: CameraPreset): boolean;
  fitToModel(): boolean;
  getModelBounds(): ModelBounds | null;
  getCoordinateMode(): CoordinateMode;
  setLayerOpacity?(layerId: string, opacity: number): void;
  /** Optional engine-side picker toggle (used by the "Pick" CTA). */
  togglePicking?(): void;
  isPicking?(): boolean;
  /** Optional model-tree mutators consumed by the tree section. */
  isolateNode?(node: ModelTreeNode): void;
  showAllNodes?(): void;
  hideNode?(node: ModelTreeNode): void;
  showNode?(node: ModelTreeNode): void;
  /** Optional model-tree getter used by the dock path. */
  getModelTree?(): readonly ModelTreeNode[];
  /** Optional rich selection snapshot from engine-side picking. */
  getLastPickedFeatureSelection?(): ThreeDFeatureSelection | null;
  /** Optional: return a section/clip adapter from the same engine. */
  getSectionAndClipAdapter?(): import('./SectionAndClipToolCard').SectionAndClipCommandAdapter;
  /**
   * Optional 3D Tiles level-of-detail override (operator visualization control):
   *   'auto' = distance-driven (default), 'high' = force finest/textured,
   *   'low' = force coarse blocks. Implemented on engines that stream 3D Tiles.
   */
  setTilesetLodMode?(mode: 'auto' | 'high' | 'low'): boolean;
  getTilesetLodMode?(): 'auto' | 'high' | 'low';
  /** Optional: true when a 3D Tiles layer is mounted (drives LoD control). */
  hasTilesetLayers?(): boolean;
}

// ---------------------------------------------------------------------------
// Component props
// ---------------------------------------------------------------------------

export interface ThreeDModelToolCardProps {
  /** Engine adapter — `null` when no 3D engine is mounted. */
  adapter: ThreeDModelCommandAdapter | null;
  /** Active model layer, when known. Drives the badge + source chip. */
  layer?: Layer | null;
  /** Optional explicit kind override (when `layer.kind` is ambiguous). */
  modelKind?: ThreeDModelKind;
  /** Selected feature surfaced by the picker — drives the property card. */
  selection?: ThreeDFeatureSelection | null;
  /** Optional pre-built model tree (project-level layer summary, etc.). */
  modelTree?: readonly ModelTreeNode[];
  /** Optional handler for picking the selected feature in the canvas. */
  onPickRequest?: () => void;
  /** Force the card visible regardless of the selection contract. */
  forceVisible?: boolean;
  /** Optional close handler — when supplied the card surfaces an X button. */
  onClose?: () => void;
  /** Optional override for the snapshot polling cadence in ms (tests). */
  pollIntervalMs?: number;
  'data-testid'?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const RENDER_MODES: ReadonlyArray<{
  readonly mode: ThreeDRenderMode;
  readonly label: string;
  readonly icon:
    | 'palette'
    | 'view_quilt'
    | 'view_compact'
    | 'texture';
  readonly hint: string;
}> = [
  { mode: 'default', label: 'Shaded', icon: 'view_quilt', hint: 'Shaded — full color with PBR materials' },
  { mode: 'greyscale', label: 'Greyscale', icon: 'palette', hint: 'Greyscale — form and shape only' },
  { mode: 'xray', label: 'X-Ray', icon: 'view_compact', hint: 'X-Ray — semi-transparent for hidden geometry' },
  { mode: 'wireframe', label: 'Wireframe', icon: 'texture', hint: 'Wireframe overlay — edges and topology' },
] as const;

const LOD_MODES: ReadonlyArray<{
  readonly mode: 'auto' | 'high' | 'low';
  readonly label: string;
  readonly icon: IconName;
  readonly hint: string;
}> = [
  { mode: 'auto', label: 'Auto', icon: 'auto_awesome', hint: 'Auto — detail follows camera distance (default)' },
  { mode: 'high', label: 'High detail', icon: 'layers', hint: 'Force the finest textured LoD at any distance' },
  { mode: 'low', label: 'Low detail', icon: 'layers_clear', hint: 'Force the coarse block LoD — lightweight overview' },
] as const;

const PRESET_ICON: Record<CameraPreset, 'photo_camera' | 'home' | '360' | 'crop_free'> = {
  top: 'photo_camera',
  front: 'photo_camera',
  back: 'photo_camera',
  right: 'photo_camera',
  left: 'photo_camera',
  iso: '360',
  fit: 'home',
};

export function ThreeDModelToolCard({
  adapter,
  layer,
  modelKind,
  selection,
  modelTree,
  onPickRequest,
  forceVisible = false,
  onClose,
  pollIntervalMs = 1500,
  'data-testid': testId,
}: ThreeDModelToolCardProps) {
  type SavedViewFilter = 'all' | 'selection';
  const sceneSelectionFromHook = useViewerStore((s) => s.sceneSelection);
  const liveSceneSelection = useViewerStore.getState().sceneSelection;
  const sceneSelection =
    sceneSelectionFromHook.kind === 'cleared'
      ? liveSceneSelection
      : sceneSelectionFromHook;

  const savedViews = useViewerStore((s) => s.savedViews);
  const saveCurrentView = useViewerStore((s) => s.saveCurrentView);
  const restoreSavedView = useViewerStore((s) => s.restoreSavedView);
  const removeSavedView = useViewerStore((s) => s.removeSavedView);

  const [eventToolCardKind, setEventToolCardKind] = useState<string | null>(null);
  const [savedViewFilter, setSavedViewFilter] = useState<SavedViewFilter>('all');
  const [renderMode, setRenderModeState] = useState<ThreeDRenderMode>(() =>
    adapter?.getRenderMode() ?? 'default',
  );
  const [bounds, setBounds] = useState<ModelBounds | null>(() =>
    adapter?.getModelBounds() ?? null,
  );
  const [lodMode, setLodModeState] = useState<'auto' | 'high' | 'low'>(
    () => adapter?.getTilesetLodMode?.() ?? 'auto',
  );
  const [hasTilesets, setHasTilesets] = useState<boolean>(
    () => adapter?.hasTilesetLayers?.() ?? false,
  );
  const [liveModelTree, setLiveModelTree] = useState<readonly ModelTreeNode[]>(
    () => modelTree ?? adapter?.getModelTree?.() ?? [],
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<ToolCardRequestDetail>).detail;
      if (!detail) return;
      setEventToolCardKind(detail.toolCardKind);
    };
    window.addEventListener(TOOL_CARD_REQUEST_EVENT, handler);
    return () => window.removeEventListener(TOOL_CARD_REQUEST_EVENT, handler);
  }, []);

  const selectionActivates =
    sceneSelection.toolCardKind === 'bim' ||
    sceneSelection.toolCardKind === '3dtiles' ||
    sceneSelection.layerKind === 'bim' ||
    sceneSelection.layerKind === 'glb' ||
    sceneSelection.layerKind === '3dtiles' ||
    sceneSelection.layerKind === 'cityjson-3dtiles';
  const eventActivates =
    eventToolCardKind === 'bim' || eventToolCardKind === '3dtiles';
  const visible = forceVisible || selectionActivates || eventActivates;

  const refresh = useCallback(() => {
    if (!adapter) {
      setBounds(null);
      setLiveModelTree(modelTree ?? []);
      return;
    }
    setBounds(adapter.getModelBounds());
    setRenderModeState(adapter.getRenderMode());
    setLiveModelTree(adapter.getModelTree?.() ?? modelTree ?? []);
    setLodModeState(adapter.getTilesetLodMode?.() ?? 'auto');
    setHasTilesets(adapter.hasTilesetLayers?.() ?? false);
  }, [adapter, modelTree]);

  useEffect(() => {
    if (!adapter?.getModelTree) {
      setLiveModelTree(modelTree ?? []);
    }
  }, [adapter, modelTree]);

  useEffect(() => {
    if (!visible) return;
    refresh();
    if (pollIntervalMs <= 0 || typeof window === 'undefined') return;
    const handle = window.setInterval(refresh, pollIntervalMs);
    return () => window.clearInterval(handle);
  }, [visible, refresh, pollIntervalMs]);

  const handlePreset = useCallback(
    (preset: CameraPreset) => {
      applyCameraPreset(adapter, preset);
    },
    [adapter],
  );

  const handleRenderMode = useCallback(
    (mode: ThreeDRenderMode) => {
      const next = applyRenderMode(adapter, mode);
      setRenderModeState(next);
    },
    [adapter],
  );

  const handleLodMode = useCallback(
    (mode: 'auto' | 'high' | 'low') => {
      if (!adapter?.setTilesetLodMode) return;
      adapter.setTilesetLodMode(mode);
      setLodModeState(adapter.getTilesetLodMode?.() ?? mode);
    },
    [adapter],
  );

  const handleFit = useCallback(() => {
    adapter?.fitToModel();
  }, [adapter]);

  const handlePick = useCallback(() => {
    onPickRequest?.();
    adapter?.togglePicking?.();
  }, [adapter, onPickRequest]);

  const handleSaveView = useCallback(() => {
    saveCurrentView();
  }, [saveCurrentView]);

  if (!visible) return null;

  const resolvedKind: ThreeDModelKind =
    modelKind ?? resolveModelKind(layer ?? null);
  const coordinateMode: CoordinateMode = layer
    ? coordinateModeForLayer(layer)
    : adapter?.getCoordinateMode() ?? 'georeferenced';
  const sourceStatus = computeSourceStatus(layer ?? null);
  const isPicking = adapter?.isPicking?.() ?? false;
  const resolvedModelTree = liveModelTree;
  const selectionViewCount = savedViews.filter((view) =>
    savedViewHasSelection(view),
  ).length;
  const visibleSavedViews = (() => {
    const filtered =
      savedViewFilter === 'selection'
        ? savedViews.filter((view) => savedViewHasSelection(view))
        : savedViews;
    // Keep selection-bearing views first while preserving original
    // relative order within each group.
    return [...filtered].sort((a, b) => {
      const aFlag = savedViewHasSelection(a) ? 1 : 0;
      const bFlag = savedViewHasSelection(b) ? 1 : 0;
      return bFlag - aFlag;
    });
  })();

  return (
    <section
      aria-label="3D model tool card"
      data-testid={testId ?? 'three-d-model-tool-card'}
      style={containerStyle}
    >
      <header style={headerStyle}>
        <div style={titleStyle}>
          <Icon name="view_in_ar" size={24} decorative />
          <span>{titleForKind(resolvedKind)}</span>
        </div>
        {onClose ? (
          <IconButton
            aria-label="Close 3D model tool card"
            onClick={onClose}
            data-testid="three-d-model-tool-card-close"
          >
            <Icon name="close" size={20} decorative />
          </IconButton>
        ) : null}
      </header>

      <div style={subtitleStyle}>
        {layer?.label ?? layer?.id ?? 'Select a 3D model layer to begin.'}
      </div>

      <div role="group" aria-label="3D model status" style={chipRowStyle}>
        <CoordinateModeBadge mode={coordinateMode} context={layer?.label} />
        <Chip
          icon={kindIcon(resolvedKind)}
          label={kindLabel(resolvedKind)}
          variant="neutral"
          aria-label={`Model kind: ${kindLabel(resolvedKind)}`}
          data-testid={`three-d-model-kind-${resolvedKind}`}
        />
        <Chip
          icon={sourceStatus.icon}
          label={sourceStatus.label}
          variant={sourceStatus.variant}
          aria-label={`Source status: ${sourceStatus.label}`}
          data-testid={`three-d-model-source-status-${sourceStatus.id}`}
        />
        {bounds ? (
          <Chip
            icon="straighten"
            label={`Diag ${formatDistance(bounds.diagonal)}`}
            variant="neutral"
            aria-label={`Model diagonal ${formatDistance(bounds.diagonal)}`}
            data-testid="three-d-model-diagonal-chip"
          />
        ) : null}
      </div>

      {coordinateMode === 'local' ? (
        <Banner
          severity="warning"
          title="Local coordinate frame"
          body={
            <span>
              This model has no real-world projection
              {layer?.label ? ` (${layer.label})` : ''}.
              Camera presets and section planes use the model&rsquo;s
              local axes; map-aligned tools are unavailable until a CRS
              is registered.
            </span>
          }
          narrationKey={`tdm-local-${layer?.id ?? 'unknown'}`}
          data-testid="three-d-model-local-frame-banner"
        />
      ) : null}

      <SectionTitle icon="photo_camera" label="Camera presets" />
      <div role="group" aria-label="Camera presets" style={chipRowStyle}>
        {CAMERA_PRESETS.map((preset) => (
          <ActionChip
            key={preset}
            icon={PRESET_ICON[preset]}
            label={cameraPresetLabel(preset)}
            disabled={!adapter || !bounds}
            onClick={() => handlePreset(preset)}
            ariaLabel={`Apply ${cameraPresetLabel(preset)} view`}
            testId={`three-d-model-preset-${preset}`}
          />
        ))}
      </div>

      <SectionTitle icon="palette" label="Render mode" />
      <div role="group" aria-label="Render mode" style={chipRowStyle}>
        {RENDER_MODES.map((entry) => {
          const active = renderMode === entry.mode;
          return (
            <ActionChip
              key={entry.mode}
              icon={entry.icon}
              label={`${entry.label}${active ? ' (active)' : ''}`}
              active={active}
              disabled={!adapter}
              onClick={() => handleRenderMode(entry.mode)}
              ariaLabel={entry.hint}
              testId={`three-d-model-render-${entry.mode}`}
            />
          );
        })}
      </div>

      {hasTilesets && adapter?.setTilesetLodMode ? (
        <>
          <SectionTitle icon="layers" label="Level of detail" />
          <div role="group" aria-label="Level of detail" style={chipRowStyle}>
            {LOD_MODES.map((entry) => {
              const active = lodMode === entry.mode;
              return (
                <ActionChip
                  key={entry.mode}
                  icon={entry.icon}
                  label={`${entry.label}${active ? ' (active)' : ''}`}
                  active={active}
                  disabled={!adapter}
                  onClick={() => handleLodMode(entry.mode)}
                  ariaLabel={entry.hint}
                  testId={`three-d-model-lod-${entry.mode}`}
                />
              );
            })}
          </div>
        </>
      ) : null}

      <SectionTitle icon="account_tree" label="Model tree" />
      {resolvedModelTree.length > 0 ? (
        <ModelTreeList
          nodes={resolvedModelTree}
          selectedId={resolveTreeSelectionId(selection)}
          onIsolate={(node) => adapter?.isolateNode?.(node)}
          onShowAll={() => adapter?.showAllNodes?.()}
          onHide={(node) => adapter?.hideNode?.(node)}
          onShow={(node) => adapter?.showNode?.(node)}
        />
      ) : (
        <Banner
          severity="info"
          title="Model tree available in canvas"
          body="Open the in-canvas Tree button to explore merged LOD groups; the right-rail tree mirrors the project scene tree only."
          narrationKey="tdm-tree-empty"
          data-testid="three-d-model-tree-empty-banner"
        />
      )}

      <SectionTitle icon="info" label="Selected feature" />
      {selection ? (
        <FeaturePropertyCard selection={selection} />
      ) : (
        <p style={emptyTextStyle} data-testid="three-d-model-no-selection">
          Pick an object in the canvas to see BIM properties and the
          IfcGUID.
        </p>
      )}

      <SectionTitle icon="bookmark" label="Saved views" />
      <div style={savedViewsHeaderStyle}>
        <span style={savedViewsCountStyle}>
          {savedViews.length === 0
            ? 'No saved views yet.'
            : `${savedViews.length} view${savedViews.length === 1 ? '' : 's'}`}
        </span>
        <Button
          variant="ghost"
          onClick={handleSaveView}
          aria-label="Save current view"
          data-testid="three-d-model-save-view"
        >
          <Icon name="add" size={20} decorative /> Save
        </Button>
      </div>
      {savedViews.length > 0 ? (
        <div style={savedViewsFilterRowStyle}>
          <ActionChip
            icon="widgets"
            label={`All (${savedViews.length})`}
            active={savedViewFilter === 'all'}
            onClick={() => setSavedViewFilter('all')}
            ariaLabel="Show all saved views"
            testId="three-d-model-filter-all"
          />
          <ActionChip
            icon="category"
            label={`With selection (${selectionViewCount})`}
            active={savedViewFilter === 'selection'}
            onClick={() => setSavedViewFilter('selection')}
            ariaLabel="Show saved views with selection"
            testId="three-d-model-filter-selection"
          />
        </div>
      ) : null}
      {visibleSavedViews.length > 0 ? (
        <ul style={savedViewsListStyle} data-testid="three-d-model-saved-views">
          {visibleSavedViews.map((view) => (
            <li key={view.id} style={savedViewRowStyle}>
              <button
                type="button"
                onClick={() => restoreSavedView(view.id)}
                style={savedViewActivateBtnStyle}
                aria-label={`Restore ${view.name}`}
              >
                <Icon name="bookmark" size={20} decorative />
                <span>{view.name}</span>
                {savedViewHasSelection(view) ? (
                  <span style={savedViewSelectionBadgeStyle}>
                    <Icon name="category" size={16} decorative />
                    <span>Selection</span>
                  </span>
                ) : null}
              </button>
              <IconButton
                aria-label={`Remove ${view.name}`}
                onClick={() => removeSavedView(view.id)}
                data-testid={`three-d-model-remove-view-${view.id}`}
              >
                <Icon name="delete" size={20} decorative />
              </IconButton>
            </li>
          ))}
        </ul>
      ) : savedViews.length > 0 ? (
        <p style={emptyTextStyle}>No saved views match this filter.</p>
      ) : null}

      <footer style={footerStyle}>
        <Button
          variant="ghost"
          onClick={handlePick}
          aria-pressed={isPicking}
          aria-label={isPicking ? 'Stop picking' : 'Pick object in canvas'}
          data-testid="three-d-model-pick"
        >
          <Icon name="category" size={20} decorative />
          <span>{isPicking ? 'Stop pick' : 'Pick object'}</span>
        </Button>
        <Button
          variant="secondary"
          onClick={handleFit}
          disabled={!adapter || !bounds}
          aria-label="Fit camera to model bounds"
          data-testid="three-d-model-fit"
        >
          <Icon name="home" size={20} decorative />
          <span>Fit to model</span>
        </Button>
      </footer>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Helpers (pure) — exported for test-driven verification.
// ---------------------------------------------------------------------------

export function applyCameraPreset(
  adapter: ThreeDModelCommandAdapter | null,
  preset: CameraPreset,
): boolean {
  if (!adapter) return false;
  return adapter.setCameraPreset(preset);
}

export function applyRenderMode(
  adapter: ThreeDModelCommandAdapter | null,
  next: ThreeDRenderMode,
): ThreeDRenderMode {
  if (!adapter) return next;
  adapter.setRenderMode(next);
  return adapter.getRenderMode();
}

export function nextRenderMode(current: ThreeDRenderMode): ThreeDRenderMode {
  const order: readonly ThreeDRenderMode[] = ['default', 'greyscale', 'xray', 'wireframe'];
  const index = order.indexOf(current);
  return order[(index + 1) % order.length];
}

export function resolveModelKind(layer: Layer | null): ThreeDModelKind {
  if (!layer) return 'bim';
  const kind = String(layer.kind ?? '').toLowerCase();
  if (kind.includes('3dtiles') || kind === 'cityjson-3dtiles') return '3dtiles';
  if (kind === 'glb') return 'glb';
  return 'bim';
}

export function formatDistance(m: number): string {
  if (!Number.isFinite(m) || m <= 0) return '0 m';
  if (m >= 1000) return `${(m / 1000).toFixed(2)} km`;
  if (m >= 1) return `${m.toFixed(2)} m`;
  return `${(m * 100).toFixed(1)} cm`;
}

interface SourceStatus {
  readonly id: 'loaded' | 'partial' | 'failed' | 'unknown';
  readonly label: string;
  readonly icon: 'check_circle' | 'sync_problem' | 'error' | 'info';
  readonly variant: ChipVariant;
}

function computeSourceStatus(layer: Layer | null): SourceStatus {
  if (!layer) {
    return {
      id: 'unknown',
      label: 'No layer',
      icon: 'info',
      variant: 'neutral',
    };
  }
  return {
    id: 'loaded',
    label: 'Source loaded',
    icon: 'check_circle',
    variant: 'success',
  };
}

function titleForKind(kind: ThreeDModelKind): string {
  switch (kind) {
    case '3dtiles':
      return '3D Tiles';
    case 'glb':
      return 'glTF / GLB';
    case 'bim':
    default:
      return 'Engineering model';
  }
}

function kindLabel(kind: ThreeDModelKind): string {
  switch (kind) {
    case '3dtiles':
      return '3D Tiles';
    case 'glb':
      return 'glTF';
    case 'bim':
    default:
      return 'BIM';
  }
}

function kindIcon(
  kind: ThreeDModelKind,
): 'view_in_ar' | 'category' | 'architecture' {
  switch (kind) {
    case '3dtiles':
      return 'view_in_ar';
    case 'glb':
      return 'category';
    case 'bim':
    default:
      return 'architecture';
  }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SectionTitle({
  icon,
  label,
}: {
  icon: 'photo_camera' | 'palette' | 'account_tree' | 'info' | 'bookmark' | 'crop_free' | 'layers';
  label: string;
}) {
  return (
    <div role="separator" aria-label={label} style={sectionTitleStyle}>
      <Icon name={icon} size={20} decorative />
      <span>{label}</span>
    </div>
  );
}

function FeaturePropertyCard({ selection }: { selection: ThreeDFeatureSelection }) {
  return (
    <div
      style={propertyCardStyle}
      data-testid="three-d-model-property-card"
    >
      {selection.objectName ? (
        <PropertyRow label="Object" value={selection.objectName} />
      ) : null}
      {selection.materialName ? (
        <PropertyRow label="Material" value={selection.materialName} />
      ) : null}
      {selection.featureId ? (
        <PropertyRow label="Feature ID" value={selection.featureId} mono />
      ) : null}
      {selection.ifcGuid ? (
        <PropertyRow
          label="IfcGUID"
          value={selection.ifcGuid}
          mono
          data-testid="three-d-model-ifc-guid"
        />
      ) : null}
      {(selection.properties ?? []).map((prop) => (
        <PropertyRow
          key={prop.key}
          label={prop.key}
          value={prop.value}
          mono={isLikelyIdentifier(prop.key, prop.value)}
        />
      ))}
    </div>
  );
}

function PropertyRow({
  label,
  value,
  mono,
  'data-testid': testId,
}: {
  label: string;
  value: string;
  mono?: boolean;
  'data-testid'?: string;
}) {
  return (
    <div style={propertyRowStyle} data-testid={testId}>
      <span style={propertyLabelStyle}>{label}</span>
      <span
        style={mono ? propertyValueMonoStyle : propertyValueStyle}
      >
        {value}
      </span>
    </div>
  );
}

function isLikelyIdentifier(key: string, value: string): boolean {
  const k = key.toLowerCase();
  if (k.includes('guid') || k.includes('uuid') || k.includes('id')) return true;
  if (/^[0-9a-fA-F-]{16,}$/.test(value)) return true;
  return false;
}

function ModelTreeList({
  nodes,
  selectedId,
  onIsolate,
  onShowAll,
  onHide,
  onShow,
}: {
  nodes: readonly ModelTreeNode[];
  selectedId: string | null;
  onIsolate: (node: ModelTreeNode) => void;
  onShowAll: () => void;
  onHide: (node: ModelTreeNode) => void;
  onShow: (node: ModelTreeNode) => void;
}) {
  const flat = useMemo(() => flattenTree(nodes), [nodes]);
  return (
    <div
      role="tree"
      aria-label="3D model tree"
      style={treeContainerStyle}
      data-testid="three-d-model-tree"
    >
      <div style={treeToolbarStyle}>
        <Button
          variant="ghost"
          onClick={onShowAll}
          aria-label="Show all model nodes"
          data-testid="three-d-model-tree-show-all"
        >
          <Icon name="visibility" size={20} decorative /> Show all
        </Button>
      </div>
      {flat.map((entry) => {
        const isSelected =
          entry.node.id === selectedId ||
          entry.node.featureId === selectedId;
        return (
          <div
            key={entry.pathKey}
            role="treeitem"
            aria-selected={isSelected}
            style={treeRowStyle(entry.depth, isSelected)}
            data-testid={`three-d-model-tree-row-${entry.node.id}`}
          >
            <IconButton
              aria-label={
                entry.node.visible
                  ? `Hide ${entry.node.name}`
                  : `Show ${entry.node.name}`
              }
              onClick={() =>
                entry.node.visible ? onHide(entry.node) : onShow(entry.node)
              }
              data-testid={`three-d-model-tree-toggle-${entry.node.id}`}
            >
              <Icon
                name={entry.node.visible ? 'visibility' : 'visibility_off'}
                size={20}
                decorative
              />
            </IconButton>
            <span style={treeNameStyle}>
              {entry.node.name}
              {typeof entry.node.count === 'number' ? ` (${entry.node.count})` : ''}
            </span>
            <Button
              variant="ghost"
              onClick={() => onIsolate(entry.node)}
              aria-label={`Isolate ${entry.node.name}`}
              data-testid={`three-d-model-tree-isolate-${entry.node.id}`}
            >
              <Icon name="filter_center_focus" size={20} decorative />
            </Button>
          </div>
        );
      })}
    </div>
  );
}

function flattenTree(
  nodes: readonly ModelTreeNode[],
  depth = 0,
  parentPath = 'root',
): Array<{ node: ModelTreeNode; depth: number; pathKey: string }> {
  const out: Array<{ node: ModelTreeNode; depth: number; pathKey: string }> = [];
  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index];
    const pathKey = `${parentPath}/${depth}:${index}:${node.id}`;
    out.push({ node, depth, pathKey });
    if (node.children && node.children.length > 0) {
      out.push(...flattenTree(node.children, depth + 1, pathKey));
    }
  }
  return out;
}

function resolveTreeSelectionId(
  selection: ThreeDFeatureSelection | null | undefined,
): string | null {
  if (typeof selection?.nodeId === 'string' && selection.nodeId.trim().length > 0) {
    return selection.nodeId;
  }
  if (typeof selection?.featureId === 'string' && selection.featureId.trim().length > 0) {
    return selection.featureId;
  }
  return null;
}

function savedViewHasSelection(
  view: { sceneSelection?: { kind?: string } | null },
): boolean {
  const kind = view.sceneSelection?.kind;
  return typeof kind === 'string' && kind !== 'cleared';
}

function ActionChip({
  icon,
  label,
  active = false,
  disabled = false,
  onClick,
  ariaLabel,
  testId,
}: {
  icon: IconName;
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  ariaLabel: string;
  testId?: string;
}) {
  const styleObj: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.space[1],
    minHeight: tokens.density.touchMin,
    padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
    borderRadius: tokens.radius.chip,
    border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
    background: active
      ? tokens.color.accent.subtle
      : disabled
        ? tokens.color.surface[2]
        : tokens.color.surface[1],
    color: disabled ? tokens.color.ink.muted : tokens.color.ink.primary,
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.role.label.size,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-disabled={disabled}
      aria-pressed={active}
      aria-label={ariaLabel}
      data-testid={testId}
      style={styleObj}
    >
      <Icon name={icon} size={20} decorative />
      <span>{label}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Styles (token-only)
// ---------------------------------------------------------------------------

const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.space[3],
  background: tokens.color.surface[2],
  border: tokens.border.thin,
  borderRadius: tokens.radius.lg,
  padding: tokens.space[3],
  width: tokens.width.panelMd,
  boxShadow: tokens.elevation[1],
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: tokens.space[2],
};

const titleStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: tokens.space[2],
  color: tokens.color.ink.primary,
  fontFamily: tokens.font.family.ui,
  fontSize: tokens.font.size.lg,
  fontWeight: tokens.font.weight.semibold,
};

const subtitleStyle: CSSProperties = {
  color: tokens.color.ink.secondary,
  fontFamily: tokens.font.family.ui,
  fontSize: tokens.font.role.body.size,
};

const chipRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: tokens.space[1],
  alignItems: 'center',
};

const sectionTitleStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: tokens.space[2],
  color: tokens.color.ink.secondary,
  fontFamily: tokens.font.family.ui,
  fontSize: tokens.font.size.sm,
  fontWeight: tokens.font.weight.medium,
  textTransform: 'uppercase',
  letterSpacing: tokens.border.width.thin,
  borderTop: tokens.border.thin,
  paddingTop: tokens.space[2],
};

const propertyCardStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.space[1],
  border: tokens.border.thin,
  borderRadius: tokens.radius.md,
  background: tokens.color.surface[1],
  padding: tokens.space[2],
};

const propertyRowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '40% 60%',
  alignItems: 'baseline',
  gap: tokens.space[1],
  minHeight: tokens.density.rowDense,
};

const propertyLabelStyle: CSSProperties = {
  fontFamily: tokens.font.family.ui,
  fontSize: tokens.font.size.xs,
  color: tokens.color.ink.muted,
};

const propertyValueStyle: CSSProperties = {
  fontFamily: tokens.font.family.ui,
  fontSize: tokens.font.size.sm,
  color: tokens.color.ink.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const propertyValueMonoStyle: CSSProperties = {
  fontFamily: tokens.font.family.mono,
  fontSize: tokens.font.size.sm,
  color: tokens.color.ink.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const emptyTextStyle: CSSProperties = {
  margin: 0,
  color: tokens.color.ink.muted,
  fontFamily: tokens.font.family.ui,
  fontSize: tokens.font.size.sm,
};

const savedViewsHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: tokens.space[2],
};

const savedViewsCountStyle: CSSProperties = {
  color: tokens.color.ink.secondary,
  fontFamily: tokens.font.family.ui,
  fontSize: tokens.font.size.sm,
};

const savedViewsFilterRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: tokens.space[1],
};

const savedViewsListStyle: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.space[1],
};

const savedViewRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: tokens.space[1],
  border: tokens.border.thin,
  borderRadius: tokens.radius.md,
  padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
  background: tokens.color.surface[1],
};

const savedViewActivateBtnStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: tokens.space[1],
  background: 'transparent',
  border: 0,
  color: tokens.color.ink.primary,
  fontFamily: tokens.font.family.ui,
  fontSize: tokens.font.size.sm,
  cursor: 'pointer',
  flex: 1,
  textAlign: 'left',
  minHeight: tokens.density.touchMin,
  padding: 0,
};

const savedViewSelectionBadgeStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: tokens.space[1],
  marginInlineStart: tokens.space[2],
  padding: `0 ${tokens.space[1]}px`,
  borderRadius: tokens.radius.chip,
  border: tokens.border.thin,
  background: tokens.color.accent.subtle,
  color: tokens.color.accent.primary,
  fontFamily: tokens.font.family.ui,
  fontSize: tokens.font.size.xs,
};

const treeContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.space[1],
  border: tokens.border.thin,
  borderRadius: tokens.radius.md,
  background: tokens.color.surface[1],
  padding: tokens.space[1],
  maxHeight: tokens.size.dropdownMaxHeight / 2,
  overflowY: 'auto',
};

const treeToolbarStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  borderBottom: tokens.border.thin,
  paddingBottom: tokens.space[1],
};

function treeRowStyle(depth: number, selected: boolean): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.space[1],
    paddingLeft: tokens.space[2] + depth * tokens.space[3],
    paddingRight: tokens.space[2],
    minHeight: tokens.density.rowDefault,
    background: selected ? tokens.color.accent.subtle : 'transparent',
    borderRadius: tokens.radius.sm,
  };
}

const treeNameStyle: CSSProperties = {
  flex: 1,
  fontFamily: tokens.font.family.ui,
  fontSize: tokens.font.size.sm,
  color: tokens.color.ink.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const footerStyle: CSSProperties = {
  display: 'flex',
  gap: tokens.space[2],
  alignItems: 'center',
  justifyContent: 'flex-end',
  flexWrap: 'wrap',
};
